import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isEqual } from "lodash";
// components
import StaffItemMobile from "../StaffItemMobile";
import { SortArrows } from "../SortArrows";
import SecondaryButton from "../SecondaryButton";
import { useViewport } from "../ViewportProvider";
// icons
import { UpArrow, DownArrow } from "../../Styles/Icons/DesignSystem";
// services
import { getUserProperties } from "@intelligentlilli/api-layer";
import { mobileBreakpoint } from "../../Services/config";
import { updateStaffMember } from "../../State/slices/staff-slice";
import { sortByLastSeen, sortByUserName } from "../../Services/Utils";
import { downloadCSV } from "../../Services/Utils/downloadCSV";
import FriendsAndFamilyItem from "../FriendsAndFamilyItem";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

const DashboardFriendsAndFamily = ({ searchValue }) => {
  const [filteredStaff, setFilteredStaff] = useState([]);
  // Sort state: only one can be active at a time. undefined = unsorted.
  const [sortUser, setSortUser] = useState(
    /** @type {boolean | undefined} */ (undefined)
  );
  const [sortLastSeen, setSortLastSeen] = useState(
    /** @type {boolean | undefined} */ (undefined)
  );
  const track = useAmplitudeTracking();

  const dispatch = useDispatch();
  const staff = useSelector((state) => state.staff);
  const friendsAndFamilyUsers = useSelector(
    (state) =>
      state.staff.filter((staff) => staff.roles?.includes("friend-or-family")),
    isEqual
  );
  const server = useSelector((state) => state.session.server);
  const navigate = useNavigate();
  const { width } = useViewport();
  const onMobile = width < mobileBreakpoint;

  const augmentedFriendsAndFamily = friendsAndFamilyUsers?.map((staff) => {
    return {
      ...staff,
      serviceUsersString: staff.hubs[0]?.userstring,
      roles: ["Friends & family"],
    };
  });

  const augmentedFriendsAndFamilyString = JSON.stringify(
    augmentedFriendsAndFamily
  );

  const staffIdsString = JSON.stringify(staff.map((staff) => staff.id));

  useEffect(() => {
    if (searchValue) {
      const filteredStaff = augmentedFriendsAndFamily.filter((staff) => {
        // check whether the surname matches the search value
        const surnameMatch =
          staff.surname?.slice(0, searchValue.length).toLowerCase() ===
          searchValue.toLowerCase();
        const firstNameMatch =
          staff.forenames?.slice(0, searchValue.length).toLowerCase() ===
          searchValue.toLowerCase();
        return surnameMatch || firstNameMatch;
      });
      setFilteredStaff(filteredStaff);
    } else {
      setFilteredStaff(augmentedFriendsAndFamily);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, augmentedFriendsAndFamilyString]);

  // Get request for the staff members data and photo
  useEffect(() => {
    async function fetchData() {
      const staffIds = JSON.parse(staffIdsString) || [];
      // Wait for both the getUser and getUserHubs API calls to finish
      await Promise.all(
        staffIds.map((id, index) =>
          getUserProperties(server, id, "web").then(({ data }) => {
            dispatch(
              updateStaffMember({
                index,
                key: "lastActivity",
                value: data.lastActivity,
              })
            );
          })
        )
      );
    }
    fetchData();
  }, [server, staffIdsString, dispatch]);

  const getSortedStaff = () => {
    if (sortUser !== undefined) return sortByUserName(filteredStaff, sortUser);
    if (sortLastSeen !== undefined)
      return sortByLastSeen(filteredStaff, sortLastSeen);
    return filteredStaff;
  };
  const sortedFilteredStaff = getSortedStaff();

  const handleDownloadCSV = useCallback(() => {
    track("CSV Download", { segment: "friends_and_family_list" });
    const headers = ["Name", "Resident", "Last Seen"];
    const rows = sortedFilteredStaff?.map((s) => [
      `${s.forenames ?? ""} ${s.surname ?? ""}`.trim(),
      s.serviceUsersString ?? "",
      s.lastActivity
        ? new Date(
            Number(String(s.lastActivity).slice(0, -3)) * 1000
          ).toLocaleDateString()
        : "Unknown",
    ]);
    downloadCSV(headers, rows, "friends-and-family-export.csv");
  }, [sortedFilteredStaff, track]);

  if (filteredStaff.length === 0) {
    return (
      <div className={styles.no_content_message}>
        Your residents do not have any friend and family users attached.
      </div>
    );
  }

  return (
    <>
      {!onMobile && (
        <div className={styles.export_row}>
          <SecondaryButton onClick={handleDownloadCSV}>
            Download CSV
          </SecondaryButton>
        </div>
      )}
      {!onMobile && (
        <div className={styles.table}>
          <div className={styles.table_header}>
            <div
              className={styles.sortable_header}
              onClick={() => {
                setSortLastSeen(undefined);
                setSortUser(sortUser === true ? false : true);
              }}
            >
              <span>User</span>
              {sortUser === true ? (
                <DownArrow />
              ) : sortUser === false ? (
                <UpArrow />
              ) : (
                <SortArrows />
              )}
            </div>
            <div>Resident</div>
            <div
              className={styles.sortable_header}
              onClick={() => {
                setSortUser(undefined);
                setSortLastSeen(sortLastSeen === true ? false : true);
              }}
            >
              <span>Last seen</span>
              {sortLastSeen === true ? (
                <DownArrow />
              ) : sortLastSeen === false ? (
                <UpArrow />
              ) : (
                <SortArrows />
              )}
            </div>
          </div>
          {sortedFilteredStaff.map((staff, index) => (
            <FriendsAndFamilyItem
              key={index}
              staff={staff}
              navigate={navigate}
            />
          ))}
        </div>
      )}
      {onMobile &&
        sortedFilteredStaff.map((staff, index) => (
          <StaffItemMobile key={index} staff={staff} navigate={navigate} />
        ))}
    </>
  );
};

export default DashboardFriendsAndFamily;
